import manifest from '../imageManifest.json';

export interface ImageEntry {
  /** Intrinsic width/height of the original — used to reserve layout space. */
  width: number;
  height: number;
  /** Path prefix of the generated variants, e.g. "/img/optimized/nicolas". */
  base: string;
  /** Widths (px) that were actually generated (no upscaling). */
  widths: number[];
  /** Fallback format for non-WebP browsers. */
  ext: 'jpg' | 'png';
}

const MANIFEST = manifest as Record<string, ImageEntry>;

/** Look up the optimized variants for an original public path, if any. */
export function imageEntry(src: string): ImageEntry | undefined {
  return MANIFEST[src];
}

/** `srcSet` string for a variant format, e.g. ".../foo-480.webp 480w, ...". */
export function buildSrcSet(entry: ImageEntry, ext: 'webp' | 'jpg' | 'png'): string {
  return entry.widths.map((w) => `${entry.base}-${w}.${ext} ${w}w`).join(', ');
}

/** Largest generated variant of a given format — the sensible <img> fallback src. */
export function largestVariant(entry: ImageEntry, ext: 'webp' | 'jpg' | 'png'): string {
  const w = entry.widths[entry.widths.length - 1];
  return `${entry.base}-${w}.${ext}`;
}

/**
 * Optimized WebP URL for use as a CSS `background-image`. Picks the smallest
 * generated width that is still >= the target, falling back to the original.
 */
export function optimizedBackground(src: string, targetWidth = 1920): string {
  const entry = imageEntry(src);
  if (!entry) return src;
  const w = entry.widths.find((x) => x >= targetWidth) ?? entry.widths[entry.widths.length - 1];
  return `${entry.base}-${w}.webp`;
}
